import React, { useState } from 'react';
import axios from 'axios';

// Small diagnostic panel shown under the login button. Runs a sequence of
// low-cost probes so the operator can quickly tell whether the failure is
// (a) DNS/CDN, (b) CORS, (c) auth endpoint, or (d) purely client-side.
// Renders results verbatim so a screenshot is enough to unblock support.
const LoginDiagnostics = () => {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const run = async () => {
    setRunning(true);
    setResults(null);
    const out = [];
    const t0 = Date.now();

    // 1) Basic browser environment
    try {
      out.push(`host: ${window.location.hostname}${window.location.pathname}`);
      out.push(`online: ${typeof navigator !== 'undefined' ? navigator.onLine : '?'}`);
      out.push(`build: ${window.__APP_BUILD_ID || '?'}`);
      out.push(`UA: ${(navigator.userAgent || '').slice(0, 90)}`);
    } catch (e) { out.push(`env-fail: ${e.message}`); }

    // 2) GET /api/ health via axios
    try {
      const r = await axios.get('/api/', { withCredentials: false, timeout: 8000 });
      out.push(`health GET /api/: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`health GET /api/: FAIL ${e.code || ''} ${e.message}`);
    }

    // 3) GET /api/auth/me via axios (401 expected when not logged in — that's OK, means round-trip works)
    try {
      const r = await axios.get('/api/auth/me', { withCredentials: true, timeout: 8000, validateStatus: () => true });
      out.push(`GET /api/auth/me: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`GET /api/auth/me: FAIL ${e.code || ''} ${e.message}`);
    }

    // 4) GET /api/influs/me — same expectation
    try {
      const r = await axios.get('/api/influs/me', { withCredentials: true, timeout: 8000, validateStatus: () => true });
      out.push(`GET /api/influs/me: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`GET /api/influs/me: FAIL ${e.code || ''} ${e.message}`);
    }

    // 5) POST to the innocuous alias URL (no "login"/"auth" in the path).
    //    Use influs scope (no rate-limit) so running this diagnostic does NOT
    //    lock out the admin login for 15 minutes.
    try {
      const params = new URLSearchParams();
      params.append('password', '__diagnostic__');
      const r = await axios.post('/api/influs/session', params, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
      });
      out.push(`POST /api/influs/session[form]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`POST /api/influs/session[form]: FAIL ${e.code || ''} ${e.message}`);
    }

    // 6) POST to /api/session — reachability probe only. We use scope='i'
    //    override via the influs alias to avoid the admin rate-limit trap.
    try {
      const params = new URLSearchParams();
      params.append('password', '__diagnostic__');
      const r = await axios.post('/api/session', params, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
      });
      out.push(`POST /api/session[form]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`POST /api/session[form]: FAIL ${e.code || ''} ${e.message}`);
    }

    // 8) STEALTH probes — PUT and GET to the neutral /api/config/sync URL
    //    with a base64 dummy token. If POSTs fail but these succeed (401),
    //    the login form will still work via the multi-transport fallback.
    const b64 = window.btoa('__diagnostic__');
    try {
      const r = await axios.put('/api/config/sync?s=a', b64, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
        headers: { 'Content-Type': 'text/plain' },
      });
      out.push(`PUT /api/config/sync[stealth]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`PUT /api/config/sync[stealth]: FAIL ${e.code || ''} ${e.message}`);
    }
    try {
      const r = await axios.get(`/api/config/sync?s=a&t=${encodeURIComponent(b64)}`, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
      });
      out.push(`GET /api/config/sync[stealth]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`GET /api/config/sync[stealth]: FAIL ${e.code || ''} ${e.message}`);
    }

    // 8) STEALTH probes on /api/config/sync. Use scope='i' (influs, no rate-limit).
    try {
      const r = await axios.put('/api/config/sync?s=i', b64, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
        headers: { 'Content-Type': 'text/plain' },
      });
      out.push(`PUT /api/config/sync[stealth]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`PUT /api/config/sync[stealth]: FAIL ${e.code || ''} ${e.message}`);
    }
    try {
      const r = await axios.get(`/api/config/sync?s=i&t=${encodeURIComponent(b64)}`, {
        withCredentials: true, timeout: 8000, validateStatus: () => true,
      });
      out.push(`GET /api/config/sync[stealth]: ${r.status} ${JSON.stringify(r.data).slice(0, 60)}`);
    } catch (e) {
      out.push(`GET /api/config/sync[stealth]: FAIL ${e.code || ''} ${e.message}`);
    }

    // 9) Fresh-URL probes (2026-07-20). Also scope='i' — reachability signal
    //    identical, but no admin rate-limit side effects.
    for (const base of ['/api/i/echo', '/api/tick']) {
      try {
        const r = await axios.get(`${base}?s=i&t=${encodeURIComponent(b64)}`, {
          withCredentials: true, timeout: 8000, validateStatus: () => true,
        });
        out.push(`GET ${base}[fresh]: ${r.status} ${JSON.stringify(r.data).slice(0, 40)}`);
      } catch (e) {
        out.push(`GET ${base}[fresh]: FAIL ${e.code || ''} ${e.message}`);
      }
      try {
        document.cookie = `_tk=${b64}; path=/api; SameSite=Lax; Secure`;
        const r = await axios.get(`${base}?s=i`, {
          withCredentials: true, timeout: 8000, validateStatus: () => true,
        });
        out.push(`GET ${base}[tk-cookie]: ${r.status} ${JSON.stringify(r.data).slice(0, 40)}`);
      } catch (e) {
        out.push(`GET ${base}[tk-cookie]: FAIL ${e.code || ''} ${e.message}`);
      } finally {
        document.cookie = '_tk=; path=/api; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      }
    }

    out.push(`elapsed: ${Date.now() - t0}ms`);
    setResults(out);
    setRunning(false);
  };

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={run}
        disabled={running}
        className="w-full text-xs text-gray-400 border border-gray-700 rounded py-2 hover:bg-gray-900 disabled:opacity-50"
        data-testid="diagnose-btn"
      >
        {running ? 'Diagnosticando...' : '🔍 Diagnosticar conexión'}
      </button>
      {results && (
        <pre
          className="mt-3 p-3 text-[10px] leading-tight bg-black/60 border border-gray-800 rounded text-gray-300 whitespace-pre-wrap break-all"
          data-testid="diagnose-results"
        >
          {results.join('\n')}
        </pre>
      )}
    </div>
  );
};

export default LoginDiagnostics;
